import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Keys } from '../../utils/keys';

export interface UseListboxNavigationOptions {
  /** Number of navigable items (including virtual rows such as "create" or "select all"). */
  count: number;
  open: boolean;
  isDisabled?: (index: number) => boolean;
  /** Wrap from last to first and vice versa (default true). */
  loop?: boolean;
  /** Called on Enter with the active index. */
  onSelect: (index: number) => void;
  onOpen?: () => void;
  onClose?: () => void;
  /** Page size used by PageUp / PageDown (default 10). */
  pageSize?: number;
}

export interface ListboxNavigation {
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  listboxId: string;
  optionId: (index: number) => string;
  /** `aria-activedescendant` for the combobox input (undefined while closed). */
  activeDescendant: string | undefined;
  /** Moves to the first enabled item (or -1 when none). */
  reset: (preferred?: number) => void;
  /** Handles listbox keys; returns true when the event was consumed. */
  handleKeyDown: (event: KeyboardEvent<HTMLElement>) => boolean;
}

/**
 * Roving highlight for ARIA 1.2 comboboxes / listboxes: ArrowUp/Down (wrap), Home/End, PageUp/Down,
 * Enter (select), Escape and Tab (close). Shared by Combobox, MultiSelect and TimePicker.
 */
export function useListboxNavigation(options: UseListboxNavigationOptions): ListboxNavigation {
  const { count, open, loop = true, pageSize = 10 } = options;
  // Callbacks live in a ref so inline functions never invalidate the navigation helpers.
  const callbacks = useRef(options);
  callbacks.current = options;
  const baseId = useId();
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = `${baseId}-listbox`;
  const optionId = useCallback((index: number) => `${baseId}-option-${index}`, [baseId]);

  const enabled = useCallback((i: number) => i >= 0 && i < count && !(callbacks.current.isDisabled?.(i) ?? false), [count]);

  const firstEnabled = useCallback(
    (from: number, dir: 1 | -1): number => {
      if (count === 0) return -1;
      let i = from;
      for (let n = 0; n < count; n += 1) {
        if (i < 0) i = loop ? count - 1 : 0;
        if (i >= count) i = loop ? 0 : count - 1;
        if (enabled(i)) return i;
        i += dir;
      }
      return -1;
    },
    [count, enabled, loop],
  );

  const reset = useCallback(
    (preferred = 0) => {
      setActiveIndex(preferred >= 0 && enabled(preferred) ? preferred : firstEnabled(0, 1));
    },
    [enabled, firstEnabled],
  );

  // Keep the highlight valid when the list shrinks.
  useEffect(() => {
    if (activeIndex >= count) setActiveIndex(count === 0 ? -1 : firstEnabled(count - 1, -1));
  }, [activeIndex, count, firstEnabled]);

  // Keep the highlighted option in view.
  useEffect(() => {
    if (!open || activeIndex < 0 || typeof document === 'undefined') return;
    const el = document.getElementById(optionId(activeIndex));
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex, optionId]);

  const move = useCallback(
    (delta: number) => {
      if (count === 0) return;
      if (activeIndex < 0) {
        setActiveIndex(delta > 0 ? firstEnabled(0, 1) : firstEnabled(count - 1, -1));
        return;
      }
      const dir: 1 | -1 = delta > 0 ? 1 : -1;
      let target = activeIndex + delta;
      if (Math.abs(delta) > 1) target = Math.max(0, Math.min(count - 1, target));
      else if (!loop) target = Math.max(0, Math.min(count - 1, target));
      setActiveIndex(firstEnabled(target, dir));
    },
    [activeIndex, count, firstEnabled, loop],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>): boolean => {
      const { onSelect, onOpen, onClose } = callbacks.current;
      switch (event.key) {
        case Keys.ArrowDown:
          event.preventDefault();
          if (!open) {
            onOpen?.();
            setActiveIndex(firstEnabled(0, 1));
          } else move(1);
          return true;
        case Keys.ArrowUp:
          event.preventDefault();
          if (!open) {
            onOpen?.();
            setActiveIndex(firstEnabled(count - 1, -1));
          } else move(-1);
          return true;
        case Keys.Home:
          if (!open) return false;
          event.preventDefault();
          setActiveIndex(firstEnabled(0, 1));
          return true;
        case Keys.End:
          if (!open) return false;
          event.preventDefault();
          setActiveIndex(firstEnabled(count - 1, -1));
          return true;
        case Keys.PageDown:
          if (!open) return false;
          event.preventDefault();
          move(pageSize);
          return true;
        case Keys.PageUp:
          if (!open) return false;
          event.preventDefault();
          move(-pageSize);
          return true;
        case Keys.Enter:
          if (!open) return false;
          event.preventDefault();
          if (activeIndex >= 0 && enabled(activeIndex)) onSelect(activeIndex);
          return true;
        case Keys.Escape:
          if (!open) return false;
          event.preventDefault();
          event.stopPropagation();
          onClose?.();
          return true;
        case Keys.Tab:
          if (open) onClose?.();
          return false;
        default:
          return false;
      }
    },
    [activeIndex, count, enabled, firstEnabled, move, open, pageSize],
  );

  return {
    activeIndex,
    setActiveIndex,
    listboxId,
    optionId,
    activeDescendant: open && activeIndex >= 0 ? optionId(activeIndex) : undefined,
    reset,
    handleKeyDown,
  };
}
